import axios from "axios";
import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Generator from "@/pages/Generator";
import Setup from "@/pages/Setup";
import DailyEntry from "@/pages/DailyEntry";
import FundManagement from "@/pages/FundManagement";
import DailyConsumption from "@/pages/DailyConsumption";
import MonthlyMDCF from "@/pages/MonthlyMDCF";
import ReportsHub from "@/pages/ReportsHub";
import RiceReport from "@/pages/RiceReport";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SchoolDashboard from "@/pages/SchoolDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import MigrationExport from "@/pages/MigrationExport";
import PublicSetupForm from "@/pages/PublicSetupForm";
import SchoolInbox from "@/pages/SchoolInbox";
import BillPhotos from "@/pages/BillPhotos";
import ChoosePlan from "@/pages/ChoosePlan";
import MyPlan from "@/pages/MyPlan";
import AdminFieldConfig from "@/pages/AdminFieldConfig";
import AdminWhatsappNudges from "@/pages/AdminWhatsappNudges";
import AdminPartnerGrants from "@/pages/AdminPartnerGrants";
import AdminFundCheck from "@/pages/AdminFundCheck";
import SsoToolkit from "@/pages/SsoToolkit";
import { AboutUs, ContactUs, PrivacyPolicy, TermsConditions, RefundPolicy } from "@/pages/PolicyPages";
import { Toaster } from "@/components/ui/sonner";
import OfflineBanner from "@/components/mobile/OfflineBanner";
import SiteFooter from "@/components/SiteFooter";
import { LiveActivityTicker, LiveActivityToast } from "@/components/LiveActivity";
import { ActiveSchoolProvider, useActiveSchool } from "@/lib/activeSchool";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { useSetupCompleteness } from "@/lib/useSetupCompleteness";
// iter135 — Phase 1 mobile-first chrome
import BottomNav from "@/components/mobile/BottomNav";
import AppHeader from "@/components/mobile/AppHeader";
import SlideOutDrawer from "@/components/mobile/SlideOutDrawer";
import FAB from "@/components/mobile/FAB";
// iter137 — Phase 3 additions
import PWAInstallBanner from "@/components/mobile/PWAInstallBanner";
import PageTransition from "@/components/mobile/PageTransition";
// iter151 — "What's New" bottom sheet, auto-fired once per app version.
import WhatsNewSheet from "@/components/mobile/WhatsNewSheet";

// AuthGate: requires a logged-in user, otherwise redirects to /login. When
// `requireVerified` is set (default for protected feature pages), an
// unverified school user is bounced to /dashboard so they only see the
// verification-pending screen until the admin approves them.
function AuthGate({ children, adminOnly = false, requireVerified = true, requirePlan = false }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center bg-sand-50 text-ink-700">
        <p className="text-sm">Loading…</p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  // Unverified school users are locked to /dashboard. EXCEPTION: a school
  // user who chose "self" setup may still access /setup so they can fill
  // vendors / CCH / enrolment while waiting for verification.
  if (requireVerified && user.role === "school") {
    const verifiedOk = !!user.verified;
    const au = user.activeUntil ? new Date(user.activeUntil) : null;
    const windowOk = !au || Number.isNaN(au.getTime()) || au.getTime() > Date.now();
    const accessOk = verifiedOk && windowOk;
    if (!accessOk) {
      const selfSetupExempt =
        user.setupChoice === "self" && location.pathname === "/setup";
      if (location.pathname !== "/dashboard" && !selfSetupExempt) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }
  // iter109 — Plan gating: verified school users with NO active subscription
  // are redirected to /choose-plan when attempting to access a report route.
  // Grandfather existing users who have any non-empty activePlan.
  if (requirePlan && user.role === "school" && user.verified) {
    const planKey = (user.activePlan || "").trim();
    if (!planKey && location.pathname !== "/choose-plan") {
      return <Navigate to="/choose-plan" replace />;
    }
  }
  return children;
}

// Existing GuardedRoute (no school profiles → push to setup) but only fires
// AFTER the auth check so anonymous users always see /login first.
// Also enforces "setup completeness" — once a school user is verified, the
// mandatory Setup fields (profile + vendors + CCHs + enrolment) must all be
// filled before they can land on Daily Entry / Funds / Reports / MDCF /
// Generator. /setup, /dashboard and /inbox stay accessible so the user can
// finish the missing pieces (and still chat with admin).
function GuardedRoute({ children }) {
  const { user } = useAuth();
  const { profiles, loading, hasFetched } = useActiveSchool();
  const setupCompleteness = useSetupCompleteness(
    user?.role === "school" && user?.verified ? user?.schoolId : null,
  );
  const location = useLocation();
  if (loading || !hasFetched) {
    return (
      <div className="grid min-h-screen place-items-center bg-sand-50 text-ink-700">
        <p className="text-sm">Loading…</p>
      </div>
    );
  }
  if (profiles.length === 0 && location.pathname !== "/setup") {
    return <Navigate to="/setup?onboarding=1" replace />;
  }
  // Setup-incomplete gate. Only relevant for verified school users — admins
  // and unverified users are already routed elsewhere.
  if (
    user?.role === "school" &&
    user?.verified &&
    !setupCompleteness.loading &&
    !setupCompleteness.isComplete &&
    location.pathname !== "/setup" &&
    location.pathname !== "/dashboard" &&
    location.pathname !== "/inbox"
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// AuthedShell — wraps every logged-in route with the social-proof ticker (top)
// and toast popper (bottom-right). Both surfaces poll the activity feed every
// 30s. Skipped on /login, /register and /setup-form/* because they're
// unauthenticated. iter135 — also mounts the mobile-only chrome (sticky
// header + drawer + bottom nav + route-aware FAB) so the site feels like
// an Android app on phones.
function AuthedShell({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // iter151 — "What's New" re-open from Help menu dispatches a custom
  // event; we track that as a force-open flag.
  const [whatsNewForce, setWhatsNewForce] = useState(false);
  // iter164 — admin switch for the anchor-based fund cascade (default OFF).
  useEffect(() => {
    if (!user) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/app-settings/fund-cascade`)
      .then(async ({ data }) => {
        const { setUseNewFundCascade } = await import("@/lib/fundCascade");
        const { setUseClasswiseRiceNorms } = await import("@/lib/riceNorms");
        const a = setUseNewFundCascade(data?.useNewFundCascade);
        const b = setUseClasswiseRiceNorms(data?.useClasswiseRiceNorms);
        if (a || b) {
          const { bumpDailyEntriesVersion } = await import("@/lib/masterDataBus");
          bumpDailyEntriesVersion();
        }
      })
      .catch(() => {});
  }, [user]);
  // Pages that should NOT show the mobile chrome (no auth, or full-bleed UX).
  const publicPaths = ["/login", "/register", "/sso/toolkit", "/about", "/contact", "/privacy", "/terms", "/refund"];
  const isPublicSetupForm = location.pathname.startsWith("/setup-form/");
  const hideChrome = !user || publicPaths.includes(location.pathname) || isPublicSetupForm;

  // iter151 — listen for the Help → What's New menu item.
  useEffect(() => {
    const onOpen = () => setWhatsNewForce(true);
    window.addEventListener("mdm:open-whats-new", onOpen);
    return () => window.removeEventListener("mdm:open-whats-new", onOpen);
  }, []);

  // iter151 — increment the visit counter once per auth session. Powers
  // the "2nd-visit" trigger inside PWAInstallBanner.
  useEffect(() => {
    if (!user) return;
    try {
      const n = parseInt(localStorage.getItem("mdm.visitCount") || "0", 10) || 0;
      localStorage.setItem("mdm.visitCount", String(n + 1));
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // iter146 — FAB targeting:
  //   /daily  → suppressed entirely (collides visually with the sticky Save
  //             bar, and the FAB only linked back to /daily which is the
  //             page we're already on).
  //   /dashboard → opens Daily Entry pre-selected to today's date.
  //   anywhere else → no FAB.
  let fabTarget = null;
  if (user && user.role !== "admin" && !hideChrome) {
    if (location.pathname === "/dashboard") {
      const todayIso = new Date().toISOString().slice(0, 10);
      fabTarget = { to: `/daily?date=${todayIso}`, label: "Add today's entry", testid: "fab-add-entry" };
    }
  }

  return (
    <>
      {user && <LiveActivityTicker />}
      {!hideChrome && (
        <>
          <AppHeader onOpenDrawer={() => setDrawerOpen(true)} />
          <SlideOutDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        </>
      )}
      <div className={hideChrome ? "" : "pt-14 pb-20 md:pt-0 md:pb-0"}>
        <PageTransition>{children}</PageTransition>
      </div>
      {!hideChrome && <BottomNav />}
      {fabTarget && <FAB {...fabTarget} />}
      {!hideChrome && <PWAInstallBanner />}
      {!hideChrome && (
        <WhatsNewSheet
          forceOpen={whatsNewForce}
          onDone={() => setWhatsNewForce(false)}
        />
      )}
      {user && <LiveActivityToast />}
      {/* iter149 — Offline + sync banner. Rendered at root so it sits above
          every route and chrome. Only renders when offline OR when the
          IDB queue has unsynced entries. */}
      <OfflineBanner />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ActiveSchoolProvider>
          <BrowserRouter>
            <AuthedShell>
              <Routes>
                {/* Public auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/sso/toolkit" element={<SsoToolkit />} />

                {/* Public external setup form — token-gated, no login. */}
                <Route path="/setup-form/:token" element={<PublicSetupForm />} />

                {/* iter108 — payment flows fully removed. /pay/:token route
                    deleted along with the PayRedirect page. */}

                {/* Public info / legal — no auth needed so unauthenticated users
                    can read About / Privacy from the footer of /login. */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/refund" element={<RefundPolicy />} />

                {/* School dashboard (any logged-in user — unverified users
                    see the awaiting-verification screen here). */}
                <Route path="/dashboard" element={<AuthGate requireVerified={false}><SchoolDashboard /></AuthGate>} />
                <Route path="/inbox" element={<AuthGate requireVerified={false}><SchoolInbox /></AuthGate>} />
                <Route path="/photo-bills" element={<AuthGate requireVerified={false}><BillPhotos /></AuthGate>} />
                <Route path="/choose-plan" element={<AuthGate requireVerified={false}><ChoosePlan /></AuthGate>} />
                <Route path="/my-plan" element={<AuthGate requireVerified={false}><MyPlan /></AuthGate>} />

                {/* Admin dashboard */}
                <Route path="/admin" element={<AuthGate adminOnly requireVerified={false}><AdminDashboard /></AuthGate>} />
                <Route path="/admin/migration" element={<AuthGate adminOnly requireVerified={false}><MigrationExport /></AuthGate>} />
                <Route path="/admin/field-config" element={<AuthGate adminOnly requireVerified={false}><AdminFieldConfig /></AuthGate>} />
                <Route path="/admin/nudges" element={<AuthGate adminOnly requireVerified={false}><AdminWhatsappNudges /></AuthGate>} />
                <Route path="/admin/fund-check" element={<AuthGate adminOnly requireVerified={false}><AdminFundCheck /></AuthGate>} />
                <Route path="/admin/partner-grants" element={<AuthGate adminOnly requireVerified={false}><AdminPartnerGrants /></AuthGate>} />

                {/* Existing pages — gated by auth + an active school profile + an active plan (iter109) */}
                <Route path="/" element={<AuthGate requirePlan><GuardedRoute><Generator /></GuardedRoute></AuthGate>} />
                <Route path="/setup" element={<AuthGate><Setup /></AuthGate>} />
                <Route path="/daily" element={<AuthGate requirePlan><GuardedRoute><DailyEntry /></GuardedRoute></AuthGate>} />
                <Route path="/funds" element={<AuthGate requirePlan><GuardedRoute><FundManagement /></GuardedRoute></AuthGate>} />
                <Route path="/consumption" element={<AuthGate requirePlan><GuardedRoute><DailyConsumption /></GuardedRoute></AuthGate>} />
                <Route path="/mdcf" element={<AuthGate requirePlan><GuardedRoute><MonthlyMDCF /></GuardedRoute></AuthGate>} />
                <Route path="/reports" element={<AuthGate requirePlan><GuardedRoute><ReportsHub /></GuardedRoute></AuthGate>} />
                <Route path="/rice-report" element={<AuthGate requirePlan><GuardedRoute><RiceReport /></GuardedRoute></AuthGate>} />
                <Route path="*" element={<AuthGate requirePlan><GuardedRoute><Generator /></GuardedRoute></AuthGate>} />
              </Routes>
              <SiteFooter />
            </AuthedShell>
          </BrowserRouter>
          <Toaster
            richColors
            position="top-right"
            toastOptions={{
              className: "md:!right-4 md:!top-4",
            }}
            offset={16}
            mobileOffset={{ top: "calc(56px + env(safe-area-inset-top, 0px) + 8px)" }}
          />
        </ActiveSchoolProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
